import {useTask} from "@/core/composable/useTask";
import {genqlBuild} from "@/core/graphql/genql/builder";
import {useGraphql} from "@/core/graphql/service";
import {useAlerts} from "@/core/store/alerts";
import {useNotificationQueueStore} from "@/core/store/notification-queue/store";
import {useScopeQuery} from "@/core/store/scopeQuery";
import {arrayGroup} from "@/core/util/arrayGroup";
import toInt from "@/core/util/toInt";
import {ClientNotice} from "@/gql/gen";
import {defineStore} from "pinia";
import {computed, ref} from "vue";
import {useRouter} from "vue-router";

const STORE_NAME = "notice";

export type ClientNoticeComputed = ClientNotice & {
    isReaded: boolean;
    isShown: boolean;
    age: number;
};

export const useNoticeStore = defineStore(
    STORE_NAME,
    () => {
        const {queryWrapped} = useGraphql();
        const {showAlerts, showNotices} = useAlerts();
        const notificationQueue = useNotificationQueueStore()

        const router = useRouter();

        const notices = ref<ClientNotice[]>([]);

        const readedIds = ref<Record<string, number>>({});
        const shownIds = ref<Record<string, number>>({});

        const setShownNotice = (notice: ClientNotice) => {
            shownIds.value[notice.ID] = Date.now()
        }

        const getIsReaded = (notice: ClientNotice) => {
            return (
                notice.isReaded ||
                !!readedIds.value[notice.ID] ||
                (getIsShown(notice) && !notice.haveBody)
            );
        };

        const getIsShown = (notice: ClientNotice) => {
            return notice.isReaded || shownIds.value[notice.ID]
        };

        const noticesComputed = computed(() => {
            return notices.value.map<ClientNoticeComputed>((notice) => ({
                ...notice,
                isReaded: getIsReaded(notice),
                isShown: getIsShown(notice),
                age: Math.round(Date.now() / 1000 - (notice.CREATED_AT || 0)),
            }));
        });

        const noticesNew = computed(() =>
            noticesComputed.value.filter((notice) => !notice.isReaded)
        );

        const {registerScopeQuery} = useScopeQuery();

        const noticeById = computed(() => {
            return notices.value.reduce<Record<string, ClientNotice>>(
                (map, notice) => {
                    map[notice.ID] = notice;
                    return map;
                },
                {}
            );
        });

        const unreadedCount = computed(() => {
            return noticesComputed.value.reduce((acc, item) => {
                if (!item.isReaded) acc++;
                return acc;
            }, 0);
        });

        const filterUnshownNotices = (items: ClientNotice[]) => {
            return items.filter((notice) => () => !shownIds.value[notice.ID] && !readedIds.value[notice.ID]);
        };

        const showUnshownNotices = (allItems: ClientNotice[], limit = 3) => {

            const toShowItems: ClientNotice[] = []

            const unshownItems = filterUnshownNotices(allItems)

            const privateItems = unshownItems
                .filter(notice => notice.TARGET === 'private')
                .sort((a, b) => b.ID - a.ID)

            const commonItems = unshownItems
                .filter(notice => notice.TARGET === 'common')
                .sort((a, b) => b.ID - a.ID)

            privateItems.forEach(notice => {
                if (toShowItems.length <= limit) {
                    toShowItems.push(notice)
                    setShownNotice(notice)
                } else {
                    if (!notice.IMPORTANT) {
                        setShownNotice(notice)
                    }
                }
            })

            commonItems.forEach(notice => {
                if (toShowItems.length <= limit) {
                    toShowItems.push(notice)
                    setShownNotice(notice)
                }
            })

            const itemsByShowAs = arrayGroup(toShowItems, 'SHOW_AS')

            if (itemsByShowAs['alert']) {

                /*
                showNotices(itemsByShowAs['alert']!.map<Partial<Message>>(notice => {
                    return {
                        title: notice.TITLE,
                        message: notice.MESSAGE,
                        actions: notice.actionItems,
                        type: 'info',
                        duration: 1000 * 200
                    }
                }))
                 */
            }
        };

        const fetchNoticesPolling = ref(0);

        const fetchNotices = useTask(
            async () => {
                const items = await queryWrapped<ClientNotice[]>(
                    genqlBuild({
                        notice_pub_list: {
                            __fragment: "NoticeFields",
                        },
                    }),
                    {}
                );
                if (items) showUnshownNotices(items);
            },
            {polling: fetchNoticesPolling}
        );

        const readNotice = (notice: ClientNotice) => {
            readedIds[notice.ID] = notice.ID
            shownIds[notice.ID] = notice.ID
        };

        registerScopeQuery(STORE_NAME, 'app', {
            notice_pub_list: {
                __fragment: 'NoticeFields'
            },
        }, (data) => {
            notices.value = data.notice_pub_list
            showUnshownNotices(notices.value)
        })

        const showNoticeDialog = ({id}: { id: number | string }) => {
            id = toInt(id);
            router.push("/notice/show/" + id);
        };

        const register = () => {
        };

        return {
            register,
            notices: noticesComputed,
            noticesAll: noticesComputed,
            noticesNew,
            readNotice,
            readedIds,
            unreadedCount,
            showNoticeDialog,
            noticeById,
            setShownNotice
        };
    },
    {
        /*
            persist: {
                pick: ['lastShownId'],
            }
             */
    }
);
